import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@repo/ui';
import { templateManagementApi } from '../api';

/**
 * Query key factory for tenant templates
 */
export const tenantTemplateQueryKeys = {
  all: ['tenant-templates'] as const,
  filtered: (type?: string) => ['tenant-templates', type] as const,
};

/**
 * Hook to fetch tenant templates
 * @param templateType - Optional filter by template type
 * @returns React Query result with tenant templates
 */
export function useTenantTemplates(templateType?: string) {
  return useQuery({
    queryKey: tenantTemplateQueryKeys.filtered(templateType !== 'all' ? templateType : undefined),
    queryFn: () => templateManagementApi.getTenantTemplates(templateType),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook for template upload mutation
 * @param onSuccessCallback - Optional callback to execute on successful upload
 * @returns Mutation hook for uploading templates
 */
export function useUploadTemplate(onSuccessCallback?: (data: any) => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: FormData) => {
      return templateManagementApi.uploadTemplate(formData);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: tenantTemplateQueryKeys.all });
      showToast.success('Template uploaded successfully!');
      onSuccessCallback?.(data);
    },
    onError: (error: any) => {
      showToast.error(`Upload failed: ${error.message}`);
    },
  });
}

/**
 * Hook for template deletion mutation
 * @returns Mutation hook for deleting templates
 */
export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      return templateManagementApi.deleteTemplate(templateId);
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: tenantTemplateQueryKeys.all });
      if (res?.juniperReset) {
        showToast.warning(
          'Template deleted. Juniper module configuration cleared and needs to be reconfigured.'
        );
      } else {
        showToast.success('Template deleted successfully!');
      }
      queryClient.invalidateQueries({ queryKey: ['tenant-config'] });
    },
    onError: (error: any) => {
      showToast.error(`Delete failed: ${error.message}`);
    },
  });
}

